// Import necessary modules
import { Link } from 'react-router-dom'; // For internal navigation
import { cn, getWhatsAppLink } from '../lib/utils'; // Utility functions for class names and WhatsApp link generation

// Define the props for the StepCard component
interface StepCardProps {
  number: number; // Step number to display
  title: string; // Title of the step
  description: string; // Description of the step
  link: string; // URL for navigation (internal or external)
  isExternal?: boolean; // Indicates whether the link is external
}

// StepCard Component
export function StepCard({
  number, // Step number
  title, // Step title
  description, // Step description
  link, // Navigation link
  isExternal = false, // Default is internal navigation
}: StepCardProps) {
  // Determine the wrapper component based on the link type (external or internal)
  const LinkWrapper = isExternal ? 'a' : Link;

  return (
    <LinkWrapper
      // Conditional props for internal and external links
      to={isExternal ? undefined : link} // `to` prop for internal navigation
      href={isExternal ? link : undefined} // `href` prop for external navigation
      target={isExternal ? '_blank' : undefined} // Open external links in a new tab
      rel={isExternal ? 'noopener noreferrer' : undefined} // Security attributes for external links
      className="relative bg-[#255f56] text-white p-8 rounded-xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300 cursor-pointer block"
    >
      {/* Decorative circle in the background */}
      <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/10 rounded-full transform group-hover:scale-110 transition-transform duration-300" />

      {/* Step number with opacity styling */}
      <span className="text-6xl font-bold opacity-30 absolute -right-4 -top-4">
        {`0${number}`}
      </span>

      {/* Step content */}
      <div className="relative z-10">
        <h3 className="text-2xl font-semibold mb-4">{title}</h3>
        <p className="text-white/80 leading-relaxed">{description}</p>
      </div>
    </LinkWrapper>
  );
}
